/**
 * useSessionTabOwnership — React glue for utils/sessionTabOwnership.
 *
 * Instantiates one ownership controller per sessionUid. Automatically
 * tears down when the sessionUid changes or the component unmounts.
 * Returns { role, requestOwnership } — the caller uses `role` to
 * decide whether to render read-only vs full controls.
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { createOwnership } from '../utils/sessionTabOwnership.js';

export function useSessionTabOwnership(sessionUid) {
  const [role, setRole] = useState(sessionUid ? 'unknown' : 'owner');
  const controllerRef = useRef(null);

  useEffect(() => {
    if (!sessionUid) {
      setRole('owner');
      return undefined;
    }
    const controller = createOwnership(sessionUid, {
      onChange: (next) => setRole(next),
    });
    controllerRef.current = controller;
    return () => {
      try { controller.close(); } catch { /* ignore */ }
      controllerRef.current = null;
    };
  }, [sessionUid]);

  const requestOwnership = useCallback(() => {
    controllerRef.current?.requestOwnership?.();
  }, []);

  return { role, requestOwnership };
}
